import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import { EmptyState, Panel, SegmentedControl, ToolbarButton } from '../../../ui-kit';
import { ConfirmDialog } from '../../../components/dialog';
import { useCan } from '../../../ui/PermissionsProvider';
import {
  fetchPsConfiguration, resetDemoData, setReplyMode, type PsConfiguration, type PsSettings, type ReplyMode,
} from '../../../services/policyServicingService';
import CarriersPanel from '../CarriersPanel';
import VoiceAgents from '../VoiceAgents';
import { useLive } from '../rocco/live';
import { useAgent } from '../AgentContext';

const MEANS: Record<ReplyMode, string> = {
  approve: 'Each reply waits under Live requests until someone reads it and sends it, as written or edited.',
  auto_when_sure: 'A reply the agent is sure of goes out as soon as it is written. Any other waits for someone to read and send it.',
  auto: 'Each reply goes out as soon as it is written. Requests the agent is unsure of still go to a person.',
};

const REPLY_CHOICES: { value: ReplyMode; label: string }[] = [
  { value: 'approve', label: 'Hold every reply' },
  { value: 'auto_when_sure', label: 'Send when sure' },
  { value: 'auto', label: 'Send every reply' },
];

/** Where each kind of request's answer comes from, in words. */
function answeredFrom(type: PsConfiguration['request_types'][number]): string {
  if (type.answered_from === 'person') return 'Goes to a person, with a reply drafted for them';
  if (type.answered_from === 'records') {
    const then = type.if_unanswered === 'person' ? 'goes to a person' : `asks the carrier for ${type.asks}`;
    return `Answered from the policy's records; when they do not say, ${then}`;
  }
  return `Asks the carrier for ${type.asks}`;
}

/**
 * How the Policy Servicing agent is set up, exactly as it runs.
 *
 * Every line is read from the running system. The one setting is whether a
 * reply goes out by itself or waits for a person to approve it.
 */
export default function ConfigurationPage() {
  const { agent } = useAgent();
  const fetcher = React.useCallback(() => fetchPsConfiguration(agent.id), [agent.id]);
  const { data, error, loading, reload } = useLive(fetcher, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  return (
    <Stack spacing={2}>
      <ReplySetting data={data} loading={loading} onSaved={reload} />

      <SetUp settings={data?.settings} loading={loading} />

      <Panel title="How a request moves" subtitle="From the email arriving to the reply in the same thread"
        loading={loading} loadingHeight={300}>
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box
              key={rule.when}
              sx={(t) => ({
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 1fr) minmax(200px, 1fr) 2fr' },
                columnGap: 2,
                rowGap: 0.25,
                py: 1.25,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{rule.when}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rule.then}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rule.detail}</Typography>
            </Box>
          ))}
        </Stack>
      </Panel>

      {/* The phone agent and what a call finds out: only for an agent that calls carriers. */}
      {data?.settings?.carrier_channel !== 'email' ? (
        <>
          <VoiceAgents
            agents={data?.agents ?? []}
            facts={data?.agent_facts ?? []}
            providerError={data?.provider_error}
            loading={loading}
          />

          {/* The same layout as ROCCO's: what each call finds out beside the guardrails. */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
            <Panel title="What each call finds out" subtitle="Read from the conversation when the call ends, so nobody types it in"
              loading={loading} loadingHeight={220}>
              <Stack spacing={1.25}>
                {(data?.finds_out ?? []).map((item) => <Line key={item.key} title={item.label} detail={item.detail} />)}
              </Stack>
            </Panel>

            <Panel title="Guardrails" subtitle="Held on every email and every call, whoever asks" loading={loading} loadingHeight={220}>
              <Stack spacing={1.25}>
                {(data?.guardrails ?? []).map((rail) => <Line key={rail.title} title={rail.title} detail={rail.detail} />)}
              </Stack>
            </Panel>
          </Box>
        </>
      ) : null}

      <Panel title="Requests it handles" subtitle="Anything else goes to a person" loading={loading} loadingHeight={200}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {(data?.request_types ?? []).map((type) => (
            <Line key={type.key} title={type.label} detail={answeredFrom(type)} />
          ))}
        </Box>
      </Panel>

      {data?.group_request_types?.length ? (
        <Panel title="Requests it handles for employer groups"
          subtitle="From an employer's HR contact, about one of their employees. Answered from our records where we hold the answer, and by calling the carrier where only the carrier knows">
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {data.group_request_types.map((type) => (
              <Line key={type.key} title={type.label} detail={type.answered_from} />
            ))}
          </Box>
        </Panel>
      ) : null}

      {/* Last, and folded: a long list that rarely changes, under a page that
          is otherwise about how the agent works. */}
      {data?.settings?.carrier_channel === 'email' ? (
        <Panel title="Who we email at each carrier" subtitle="Only carriers with an address are ever emailed"
          loading={loading} loadingHeight={120}>
          {data.carrier_emails?.length ? (
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              {data.carrier_emails.map((c) => <Line key={c.name} title={c.name} detail={c.email} />)}
            </Box>
          ) : (
            <EmptyState message="No carrier has an email address yet, so a question the records do not answer goes to a person." />
          )}
        </Panel>
      ) : <CarriersPanel />}

      {/* Below the carrier numbers, and only where the demo employer account exists. */}
      {data?.demo_account ? <DemoData account={data.demo_account} onReset={reload} /> : null}
    </Stack>
  );
}

/**
 * Put the demo employer account back to how a demo opens on it. A call writes
 * what the carrier said onto an employee's record, and the same question is
 * then answered from the record; this takes that off again, so the call can be
 * shown once more. It reaches nothing but that one account.
 */
function DemoData({
  account, onReset,
}: { account: NonNullable<PsConfiguration['demo_account']>; onReset: () => Promise<void> }) {
  const canManage = useCan('manage_workspace');
  const agentId = useAgent().agent.id;
  const [asking, setAsking] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const reset = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await resetDemoData(agentId);
      await onReset();
      setDone(true);
    } catch (err) {
      setDone(false);
      setProblem(err instanceof Error ? err.message : 'Could not reset the demo data');
    } finally {
      setBusy(false);
      setAsking(false);
    }
  };

  const facts = [
    `${account.requests} request${account.requests === 1 ? '' : 's'} about its employees`,
    `${account.changed_by_calls} record${account.changed_by_calls === 1 ? '' : 's'} changed by a call`,
  ].join(' · ');
  const blocked = !canManage ? 'Only a workspace admin can reset the demo data.'
    : account.on_a_call ? 'A carrier is on the phone about one of its employees. Reset once the call has ended.' : null;

  return (
    <Panel
      title="Demo data"
      subtitle={`${account.name} · Group ${account.group_number} · the employer account used to show requests from HR contacts`}
      actions={(
        <ToolbarButton variant="outlined" icon={<RestartAltRounded />} onClick={() => setAsking(true)}
          disabled={Boolean(blocked)} tooltip={blocked ?? undefined} loading={busy}>
          Reset demo data
        </ToolbarButton>
      )}
    >
      <Stack spacing={0.75}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          After a call, what the carrier said stays on the employee&apos;s record, so the same question is answered
          without calling again. Reset puts {account.name} back to how a demo opens on it, so the calls can be shown
          once more.
        </Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
          Now: {facts}.
        </Typography>
        {problem ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
        ) : done ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.success })}>
            {account.name} is back to its starting state.
          </Typography>
        ) : null}
      </Stack>
      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        title={`Reset ${account.name}?`}
        description={`${account.name}'s employees, enrollments and invoices go back to how they were before any call, `
          + 'and every request about its employees is removed from Live and Completed requests. '
          + 'Nothing else changes: requests about individual policies, carrier phone numbers and the '
          + "agent's settings are all kept."}
        confirmLabel="Reset demo data"
        onConfirm={reset}
        busy={busy}
      />
    </Panel>
  );
}

/** How this agent is set up, in words: whose it is, where its answers come from, who gets what it cannot settle. */
function SetUp({ settings, loading }: { settings: PsSettings | undefined; loading: boolean }) {
  const rows: { title: string; detail: string }[] = settings ? [
    { title: 'Writes for', detail: settings.agency_name ?? 'The agency, as the sign-off below says' },
    { title: 'Signs replies', detail: settings.sign_off.split('\n').join(' · ') },
    { title: 'Requests it handles', detail: settings.line_of_business === 'personal_lines' ? 'Home and auto' : 'Life' },
    {
      title: 'Policies come from',
      detail: settings.policy_source === 'connector'
        ? `${settings.policy_system_name ?? 'A policy system that is not connected'}: clients are found by their email address`
        : 'The workspace warehouse: the email must give the policy number',
    },
    {
      title: 'Answers from the records',
      detail: settings.answer_from_records
        ? 'Yes: the policy record and its documents, such as the declarations page, before the carrier is asked'
        : 'No: the carrier is asked',
    },
    {
      title: 'Asks carriers',
      detail: settings.carrier_channel === 'email'
        ? `By email, from this agent's inbox. No reply in ${settings.carrier_reply_wait_minutes} minutes: handed to a person`
        : 'By phone, with the AI voice agent below',
    },
    {
      title: 'What it cannot settle goes to',
      detail: [
        settings.escalation_owner_name ?? 'A person, under Completed requests',
        settings.escalation_alert === 'email' && settings.escalation_owner_email ? `emailed at ${settings.escalation_owner_email}` : null,
        settings.draft_when_handed_over ? 'with a reply drafted to check and send' : null,
      ].filter(Boolean).join(', '),
    },
    {
      title: 'Takes emails from',
      detail: [settings.policyholder_requests ? 'Policyholders' : null, settings.employer_requests ? "employers' HR contacts" : null]
        .filter(Boolean).join(' and ') || 'Nobody',
    },
  ] : [];
  return (
    <Panel title="How this agent is set up" subtitle="What this agent does differently from any other in the workspace"
      loading={loading || !settings} loadingHeight={240}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {rows.map((row) => <Line key={row.title} title={row.title} detail={row.detail} />)}
      </Box>
    </Panel>
  );
}

/** The one setting a person changes here: which replies go out by themselves. */
function ReplySetting({
  data, loading, onSaved,
}: { data: PsConfiguration | null; loading: boolean; onSaved: () => Promise<void> }) {
  const agentId = useAgent().agent.id;
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const current = data?.reply_mode ?? 'approve';

  const change = async (next: ReplyMode) => {
    if (next === current) return;
    setSaving(true);
    setProblem(null);
    try {
      await setReplyMode(agentId, next);
      await onSaved();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not change the setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Settings" subtitle="How the agent behaves in this workspace" loading={loading} loadingHeight={72}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>Replies</Typography>
          <Typography variant="caption" sx={(t) => ({ color: problem ? t.tokens.semantic.danger : t.tokens.text.tertiary })}>
            {problem ?? MEANS[current]}
          </Typography>
        </Stack>
        <SegmentedControl
          value={current}
          options={REPLY_CHOICES}
          onChange={change}
          ariaLabel="Replies"
          disabled={saving}
        />
      </Stack>
    </Panel>
  );
}

function Line({ title, detail }: { title: string; detail: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{title}</Typography>
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{detail}</Typography>
    </Stack>
  );
}
